import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Heart, PenLine, Mail, Home } from 'lucide-react'
import FloatingHearts from './FloatingHearts'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()

  const isActive = (path: string) => location.pathname === path

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden">
      <FloatingHearts />
      
      {/* Header */}
      <header className="relative z-10 bg-white/80 backdrop-blur-sm border-b border-rose-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-rose-600 rounded-full flex items-center justify-center shadow-lg shadow-rose-500/30 group-hover:scale-110 transition-transform">
                <Heart className="w-5 h-5 text-white fill-white" />
              </div>
              <span className="font-display text-xl font-semibold text-ink hidden sm:block">
                Anonim Mektuplar
              </span>
            </Link>

            <nav className="flex items-center gap-1 sm:gap-2">
              <NavLink to="/" isActive={isActive('/')}>
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">Ana Sayfa</span>
              </NavLink>
              <NavLink to="/write" isActive={isActive('/write')}>
                <PenLine className="w-4 h-4" />
                <span className="hidden sm:inline">Yaz</span>
              </NavLink>
              <NavLink to="/read" isActive={isActive('/read')}>
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">Oku</span>
              </NavLink>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 relative z-10">
        {children}
      </main>

      {/* Pöti mascot section - before footer */}
      <div className="relative z-10 flex justify-center pb-4">
        <img 
          src="/poti.png" 
          alt="Maskot" 
          className="h-40 sm:h-48 md:h-56 w-auto"
        />
      </div>

      {/* Footer */}
      <footer className="relative z-10 bg-white/60 backdrop-blur-sm border-t border-rose-100 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-ink/60 font-body">
            Made with <Heart className="w-4 h-4 inline text-rose-500 fill-rose-500" /> for Sevgiler Günü
          </p>
          <p className="text-xs text-ink/40 mt-1">
            Tüm mektuplar anonimdir ve içerik kontrolünden geçer
          </p>
        </div>
      </footer>
    </div>
  )
}

interface NavLinkProps {
  to: string
  isActive: boolean
  children: ReactNode
}

function NavLink({ to, isActive, children }: NavLinkProps) {
  return (
    <Link
      to={to}
      className={`
        flex items-center gap-1.5 px-3 py-2 rounded-lg font-body text-sm font-medium
        transition-all duration-200
        ${isActive 
          ? 'bg-rose-100 text-rose-700' 
          : 'text-ink/70 hover:bg-rose-50 hover:text-rose-600'
        }
      `}
    >
      {children}
    </Link>
  )
}
